import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Handshake, 
  Globe, 
  TrendingUp, 
  Users, 
  ArrowRight,
  Film,
  Award,
  Building2
} from 'lucide-react';
import { PageSEO } from '../components/SEO';

const services = [
  {
    icon: Globe,
    title: 'INTERNATIONAL SALES',
    description: 'We represent your film at major markets including Cannes, Berlin, AFM, and Toronto, connecting you with international buyers and distributors.',
  },
  {
    icon: Handshake,
    title: 'THEATRICAL DEALS',
    description: 'Secure theatrical distribution deals in key territories. We negotiate on your behalf to maximize exposure and revenue.',
  },
  {
    icon: TrendingUp,
    title: 'LICENSING AGREEMENTS',
    description: 'Navigate complex licensing deals for broadcast, cable, and regional streaming platforms worldwide.',
  },
  {
    icon: Users,
    title: 'BUYER RELATIONSHIPS',
    description: 'Leverage our established relationships with acquisitions executives at major studios, networks, and distributors globally.',
  },
];

const markets = [
  { name: 'Cannes Film Market', location: 'France' },
  { name: 'European Film Market', location: 'Germany' },
  { name: 'American Film Market', location: 'USA' },
  { name: 'TIFF Industry', location: 'Canada' },
  { name: 'MIPCOM', location: 'France' },
  { name: 'Hong Kong Filmart', location: 'Hong Kong' },
];

const approach = [
  {
    step: '01',
    title: 'EVALUATION',
    description: 'We assess your film\'s market potential, identifying target territories and optimal positioning strategies.',
  },
  {
    step: '02',
    title: 'PACKAGING',
    description: 'Create compelling sales materials including trailers, one-sheets, and pitch decks that resonate with buyers.',
  },
  {
    step: '03',
    title: 'MARKET PRESENCE',
    description: 'Present your film at major markets with dedicated screenings and buyer meetings.',
  },
  {
    step: '04',
    title: 'NEGOTIATION',
    description: 'Handle all deal negotiations to secure the best possible terms for your project.',
  },
];

export default function Sales() {
  return (
    <div data-testid="sales-page" className="min-h-screen pt-20">
      <PageSEO page="sales" />
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?w=1920&q=80"
            alt="Film market event - UK film sales agents representing independent cinema"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/80 to-neutral-950/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-6">
              FILM SALES AGENCY
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight uppercase leading-none text-white mb-6">
              SELL YOUR FILM<br />
              <span className="text-orange-500">WORLDWIDE</span>
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed max-w-xl">
              Beyond VOD, we act as your sales agent to secure theatrical, broadcast, and traditional distribution deals across the globe.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
              SALES SERVICES
            </span>
            <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white">
              WHAT WE OFFER
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 border border-white/10 bg-neutral-900/50 hover:bg-neutral-900 transition-colors group"
              >
                <service.icon className="w-10 h-10 text-orange-500 mb-6" />
                <h3 className="font-display text-xl tracking-wider text-white mb-4 uppercase">{service.title}</h3>
                <p className="text-neutral-400 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Markets */}
      <section className="py-24 md:py-32 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
                GLOBAL PRESENCE
              </span>
              <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white mb-6">
                WHERE WE WORK
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed mb-8">
                We maintain an active presence at all major film markets worldwide, ensuring your project gets maximum exposure to qualified buyers.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {markets.map((market, index) => (
                  <motion.div
                    key={market.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 p-4 border border-white/10 bg-neutral-900/50"
                  >
                    <Building2 className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <div>
                      <span className="text-white text-sm font-medium block">{market.name}</span>
                      <span className="text-neutral-500 text-xs">{market.location}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1524712245354-2c4e5e7121c0?w=800&q=80"
                alt="Film festival screening - international film sales representation"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
              THE PROCESS
            </span>
            <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white">
              OUR APPROACH
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approach.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 border border-white/10 bg-neutral-900/50 hover:bg-neutral-900 transition-colors relative group"
              >
                <span className="font-display text-6xl text-white/5 absolute top-4 right-4 group-hover:text-orange-500/10 transition-colors">
                  {item.step}
                </span>
                <h3 className="font-display text-xl tracking-wider text-white mb-4 uppercase">{item.title}</h3>
                <p className="text-neutral-400 leading-relaxed text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Projects */}
      <section className="py-24 md:py-32 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <img
                src="https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&q=80"
                alt="Film production set - independent film distribution and sales UK"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 to-transparent" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
                WE REPRESENT
              </span>
              <h2 className="font-display text-3xl md:text-5xl tracking-wide uppercase text-white mb-6">
                ALL GENRES WELCOME
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed mb-8">
                From compelling documentaries to narrative features, we work with filmmakers across all genres to find the right distribution partners for their unique projects.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Film className="w-6 h-6 text-orange-500" />
                  <span className="text-white">Feature Films (Drama, Thriller, Comedy, Horror)</span>
                </div>
                <div className="flex items-center gap-4">
                  <Award className="w-6 h-6 text-orange-500" />
                  <span className="text-white">Festival Award Winners</span>
                </div>
                <div className="flex items-center gap-4">
                  <Globe className="w-6 h-6 text-orange-500" />
                  <span className="text-white">International Co-Productions</span>
                </div>
                <div className="flex items-center gap-4">
                  <TrendingUp className="w-6 h-6 text-orange-500" />
                  <span className="text-white">Commercial & Art House Projects</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl md:text-5xl tracking-tight uppercase text-white mb-6">
              LET'S TALK ABOUT YOUR PROJECT
            </h2>
            <p className="text-neutral-300 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              Ready to take your film to international markets? Contact us to discuss how we can represent your project.
            </p>
            <Link
              to="/submit"
              data-testid="sales-cta-btn"
              className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-10 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
            >
              GET IN TOUCH
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
